import { Currency } from '@/modules/common/types/currency';
import { CurrencyResponse } from '@/modules/common/types/dto/currency.dto';
import { GetExchangeRatesRequest } from '@/modules/common/types/dto/request/get-exchange-rates-request';
import { ExchangeRatesResponse } from '@/modules/common/types/dto/response/exchange-rates-response';
import { ExchangeRates } from '@/modules/common/types/exchange-rates';
import { axiosInstance } from '@/utils/axios';

import { mapExchangeRatesFromResponse } from '../mappers/exchange-rates.mapper';
import { ApiErrorCode, withApiErrorHandler } from './errors';

/**
 * Fetches list of supported currencies.
 * @returns Array of Currency domain models (mapped from API response)
 */
export const getSupportedCurrencies = async (): Promise<Currency[]> => {
  const currenciesData = await withApiErrorHandler(
    '/api/currencies/supported',
    ApiErrorCode.CURRENCIES_LOAD_FAILED,
    () => axiosInstance.get<{ data: CurrencyResponse[] }>('/api/currencies/supported')
  );

  return currenciesData.map(
    currencyResponse => new Currency(currencyResponse.code, currencyResponse.displayName)
  );
};

/**
 * Fetches exchange rates for the given currencies and optional date.
 * @param params - Request parameters containing fromCodes, toCode, and optional revisionDate
 * @returns ExchangeRates domain model with rate mappings
 */
export const getExchangeRates = async (params: GetExchangeRatesRequest): Promise<ExchangeRates> => {
  const queryParams: Record<string, string> = {
    fromCodes: params.fromCodes.join(','),
    toCode: params.toCode,
  };

  if (params.revisionDate) {
    queryParams.revisionDate = params.revisionDate;
  }

  const response = await withApiErrorHandler(
    '/api/currencies/exchange-rates',
    ApiErrorCode.EXCHANGE_RATES_LOAD_FAILED,
    () =>
      axiosInstance.get<{ data: ExchangeRatesResponse }>('/api/currencies/exchange-rates', {
        params: queryParams,
      })
  );

  return mapExchangeRatesFromResponse(response);
};
